import type {ReactElement} from 'react';
import React, {useCallback, useRef} from 'react';
import cx from 'classnames';
import {v4 as uuid} from 'uuid';

import {useOnClickOutside} from '@pexip/hooks';

import {FontVariant} from '../../../../design-tokens/generated/constants';
import {TestId} from '../../../utils/testIds';
import {SvgImage} from '../SvgImage/SvgImage';
import {Text} from '../Text/Text';
import {ThemeProvider} from '../../../themes/ThemeContext';
import {Box} from '../Box/Box';
import type {ColorScheme} from '../../../types/variants';
import {ModalCloseButton} from '../../modules/CloseButton/ModalCloseButton.view';

import ArrowSvg from './arrow.svg';
import type {ControlledTooltip} from './useTooltip';
import {useTooltip} from './useTooltip';

import styles from './Tooltip.module.scss';
import {useAriaTooltip} from '../../../hooks/useAriaTooltip';

export type NotificationTooltipPosition =
    | 'topRight'
    | 'topCenter'
    | 'topLeft'
    | 'topStart'
    | 'topEnd'
    | 'bottomRight'
    | 'bottomCenter'
    | 'bottomLeft'
    | 'bottomStart'
    | 'bottomEnd';

export const NotificationTooltip: React.FC<
    React.ComponentProps<'div'> &
        ControlledTooltip & {
            usePortal?: boolean;
            position?: NotificationTooltipPosition;
            header?: React.ReactNode;
            modifier?: 'fullWidth';
            text: string | ReactElement;
            ctaText?: string | ReactElement;
            ctaFn?: () => void;
            isArrowShown?: boolean;
            testIdInner?: string;
            testIdCloseButton?: string;
            colorScheme?: ColorScheme;
            mainWrapperClassName?: string;
            closeButtonAriaLabel?: string;
            stopPointerDownPropagation?: boolean;
            shouldCloseOnOutsideClick?: boolean;
        }
> = ({
    closeButtonAriaLabel = 'Close tooltip',
    shouldCloseOnOutsideClick = false,
    colorScheme = 'light',
    position = 'topRight',
    usePortal = false,
    header,
    modifier,
    text,
    ctaText,
    ctaFn = () => {
        /*noop*/
    },
    controlledIsVisible,
    controlledOnChange,
    children,
    className,
    mainWrapperClassName,
    isArrowShown = true,
    testIdInner = TestId.NotificationTooltipInner,
    testIdCloseButton = TestId.NotificationTooltipClose,
    stopPointerDownPropagation = false,
}) => {
    const {isVisible, setVisible} = useTooltip({
        controlledIsVisible,
        controlledOnChange,
    });
    const portalIdRef = useRef(uuid());

    const close = useCallback(
        () =>
            setVisible({
                isVisible: false,
                isControlled: true,
            }),
        [setVisible],
    );

    const closeOnOutsideClick = useCallback(() => {
        shouldCloseOnOutsideClick && close();
    }, [shouldCloseOnOutsideClick, close]);

    const outsideClickRef = useOnClickOutside(closeOnOutsideClick);

    const tooltipContent = (
        <ThemeProvider colorScheme={colorScheme}>
            <Box
                data-portal-id={portalIdRef.current}
                ref={outsideClickRef}
                className={cx(
                    styles.tooltip,
                    styles.tooltipNotification,
                    styles[position],
                    modifier && styles[modifier],
                    className,
                )}
                data-testid={testIdInner}
                onPointerDownCapture={
                    stopPointerDownPropagation
                        ? e => e.stopPropagation()
                        : undefined
                }
            >
                {header && (
                    <div className={styles.headerWrapper}>
                        {typeof header === 'string' ? (
                            <Text
                                fontVariant={FontVariant.BodyBold}
                                data-testid={TestId.NotificationTooltipHeader}
                            >
                                {header}
                            </Text>
                        ) : (
                            <div data-testid={TestId.NotificationTooltipHeader}>
                                {header}
                            </div>
                        )}
                        <ModalCloseButton
                            className={styles.closeButton}
                            data-testid={testIdCloseButton}
                            onClose={close}
                            aria-label={closeButtonAriaLabel}
                        />
                    </div>
                )}
                <Text
                    fontVariant={FontVariant.Body}
                    data-testid={TestId.NotificationTooltipText}
                >
                    {text}
                </Text>
                {ctaText &&
                    (typeof ctaText === 'string' ? (
                        <Text
                            fontVariant={FontVariant.BodyLink}
                            onClick={ctaFn}
                            className={styles.cta}
                            data-testid={TestId.NotificationTooltipCta}
                        >
                            {ctaText}
                        </Text>
                    ) : (
                        ctaText
                    ))}

                {isArrowShown && (
                    <div className={styles.tooltipArrow}>
                        <SvgImage
                            source={ArrowSvg}
                            className={styles.tooltipArrowIcon}
                        />
                    </div>
                )}
            </Box>
        </ThemeProvider>
    );

    const {targetRef, tooltip} = useAriaTooltip({
        isOpen: isVisible,
        position,
        tooltipContent,
    });

    return (
        <div
            data-portal-origin-id={portalIdRef.current}
            ref={usePortal ? targetRef : undefined}
            className={cx(
                styles.tooltipContainer,
                modifier && styles[modifier],
                mainWrapperClassName,
            )}
        >
            {children}
            {!usePortal && isVisible && tooltipContent}
            {usePortal && tooltip}
        </div>
    );
};

export type NotificationTooltipProps = React.ComponentProps<
    typeof NotificationTooltip
>;
